import dayjs from "dayjs";
import { formatPercent } from "@utils";
import {
  CHANNEL_ORDER,
  MAX_TOOLTIP_SKILLS,
  MONTH_LABEL_FORMAT,
  MONTH_RANGE_FORMAT,
  SKILLS_OTHER_LABEL,
  TERM_DATE_FORMAT,
  USAGE_OVER_TIME_MAX_MONTHS
} from "./SubscriptionTab.consts";

const sumEngaged = (items) => items.reduce((sum, item) => sum + (item.engaged ?? 0), 0);

// One chart row per month: a numeric column per channel (nivo keys) plus the per-channel detail for tooltips.
export const buildChartData = (months = []) =>
  months.map(({ month, usage = [] }) => {
    const byChannel = Object.fromEntries(usage.map((entry) => [entry.channelType, entry]));
    const row = {
      month,
      label: dayjs(`${month}-01`).format(MONTH_LABEL_FORMAT),
      byChannel,
      total: 0
    };
    CHANNEL_ORDER.forEach((channelType) => {
      const engaged = byChannel[channelType]?.engaged ?? 0;
      row[channelType] = engaged;
      row.total += engaged;
    });
    return row;
  });

// Top N skills by engaged; everything else (incl. conversations with no skill) folds into "Other".
export const buildSkillsBreakdown = (skills = [], channelEngaged = 0, max = MAX_TOOLTIP_SKILLS) => {
  const sorted = [...skills].filter((skill) => skill.engaged > 0).sort((a, b) => b.engaged - a.engaged);
  const top = sorted.slice(0, max);
  const unattributed = Math.max(0, channelEngaged - sumEngaged(sorted));
  const other = sumEngaged(sorted.slice(max)) + unattributed;
  const rows = other > 0 ? [...top, { name: SKILLS_OTHER_LABEL, engaged: other }] : top;
  return rows.map((skill) => ({ ...skill, pct: formatPercent(skill.engaged, channelEngaged) }));
};

// Whole-term skills per channel (for the quota-usage bar tooltip): sum the same skill across months.
export const sumSkillsAcrossMonths = (months = []) => {
  const totals = {};
  months.forEach(({ usage = [] }) => {
    usage.forEach(({ channelType, skills = [] }) => {
      if (!totals[channelType]) totals[channelType] = {};
      skills.forEach(({ name, engaged }) => {
        totals[channelType][name] = (totals[channelType][name] ?? 0) + engaged;
      });
    });
  });
  return Object.fromEntries(
    Object.entries(totals).map(([channelType, byName]) => [
      channelType,
      Object.entries(byName).map(([name, engaged]) => ({ name, engaged }))
    ])
  );
};

// Months arrive sorted ascending ("YYYY-MM"); keep only the most recent ones for the chart.
export const limitToRecentMonths = (months = [], max = USAGE_OVER_TIME_MAX_MONTHS) =>
  [...months].sort((a, b) => a.month.localeCompare(b.month)).slice(-max);

export const formatTermRange = (start, end) => {
  if (!start || !end) return null;
  return `${dayjs(start).format(TERM_DATE_FORMAT)} - ${dayjs(end).format(TERM_DATE_FORMAT)}`;
};

// Header range for the charted months only (e.g. "Apr 2026 - Mar 2027"), so it matches what is drawn.
export const formatChartedRange = (months = []) => {
  if (!months.length) return null;
  const first = months[0].month;
  const last = months[months.length - 1].month;
  return `${dayjs(`${first}-01`).format(MONTH_RANGE_FORMAT)} - ${dayjs(`${last}-01`).format(MONTH_RANGE_FORMAT)}`;
};
